import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useDrag, useDrop, DndProvider, DragSourceMonitor } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { TableConfig } from '../../types';
import { getTableConfig, upsertTableConfig } from '../../api';
import { Column } from 'react-table';
import { table } from 'console';
import { EasyPicker } from './components/SimplePicker';

interface TableConfiguratorProps {
  objectName: string;
  screenName: string;
  subObject?: string;
  onChange: (tableColumns: Column[], save: boolean) => void;
}

interface TableConfiguratorFromColumnProps<T extends object> {
  columns: Column<T>[];
  onChange: (tableColumns: Column<T>[], save: boolean) => void;
}

const ItemType = 'COLUMN';

export const getDefaultColumns = async (objectName: string, screenName: string,subObject?:string) => {
  const tableConfig: TableConfig[] = await getTableConfig(objectName, screenName,subObject);
  const sortedTableConfig = tableConfig.sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
  return triggerOnChange(sortedTableConfig);
};

// Same as getDefaultColumns but keeps hidden (isVisible: false) entries, so callers that need
// to merge saved visibility/order into their own column set (e.g. DataTable) can see them.
export const getAllColumnsConfig = async (objectName: string, screenName: string, subObject?: string) => {
  const tableConfig: TableConfig[] = await getTableConfig(objectName, screenName, subObject);
  const sortedTableConfig = tableConfig.sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
  return buildColumns(sortedTableConfig, false);
};

interface DraggableColumnProps {
  config: TableConfig;
  index: number;
  showDeleteOption?: boolean;
  moveColumn: (dragIndex: number, hoverIndex: number) => void;
  handleColumnChange: (fieldName: string, isVisible: boolean) => void;
  handleDeleteColumn?: (fieldName: string) => void;
}

export const DraggableColumn: React.FC<DraggableColumnProps> = ({ config, index, showDeleteOption, moveColumn, handleColumnChange, handleDeleteColumn }) => {
  const ref = useRef<HTMLDivElement>(null);

  const [, drop] = useDrop({
    accept: ItemType,
    hover(item: { index: number }) {
      if (item.index !== index) {
        moveColumn(item.index, index);
        item.index = index;
      }
    },
  });

  const [{ isDragging }, drag] = useDrag({
    type: ItemType,
    item: { index },
    collect: (monitor: DragSourceMonitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  drag(drop(ref));

  return (
    <div
      ref={ref}
      className='my-2 align-items-center'
      style={{
        opacity: isDragging ? 0.5 : 1,
        cursor: 'move',
      }}
    >
      {config && (
        <label className='d-inline-flex align-items-center'>
          <span className="me-2" style={{ cursor: 'grab' }}>
            <i className="fas fa-grip-lines"></i> {/* FontAwesome grip icon */}
          </span>
          <input
            type="checkbox"
            className="me-2"
            checked={config.isVisible || false}
            onChange={(event) => handleColumnChange(config.fieldName || '', event.target.checked)}
          />
          {config.fieldHeading || config.fieldName}
          {(showDeleteOption && !config.isStandard) &&
            <span className="me-2" onClick={() =>
              handleDeleteColumn &&
              handleDeleteColumn(config.fieldName || '')}>
              <i className="ms-2 fas fa-trash" style={{ cursor: 'pointer' }}></i> {/* FontAwesome grip icon */}
            </span>
          }
          {/* <EasyPicker className='ms-3' singleValueOptions={['small','medium','large']} /> */}
        </label>
      )}
    </div>
  );
};

const buildColumns = (tableConfig: TableConfig[], filterVisible: boolean): Column[] => {
  return tableConfig
    .filter(config => !filterVisible || config.isVisible === true)
    .map(config => ({
      Header: config.fieldHeading || '',
      size: config.fieldLength || 'medium',
      accessor: config.fieldName || '',
      className: config.className || '',
      ...config
    }));
};

const triggerOnChange = (tableConfig: TableConfig[]): Column[] => buildColumns(tableConfig, true);

export const TableConfigurator: React.FC<TableConfiguratorProps> = ({ objectName, screenName, subObject, onChange }) => {
  const [configs, setConfigs] = useState<TableConfig[]>([]);

  useEffect(() => {
    fetchTableConfig();
  }, []);

  const fetchTableConfig = async () => {
    try {
      const tableConfig = await getTableConfig(objectName, screenName,subObject);
      setConfigs(tableConfig);
      triggerOnChange(tableConfig);
    } catch (error) {
      console.error('Error fetching table config:', error);
    }
  };

  const handleColumnChange = (fieldName: string) => {
    const updatedTableConfig = configs.map(config => {
      if (config.fieldName === fieldName) {
        return { ...config, isVisible: !config.isVisible };
      }
      return config;
    });

    setConfigs(updatedTableConfig);
    triggerOnChange(updatedTableConfig);
  };

  const moveColumn = useCallback((dragIndex: number, hoverIndex: number) => {
    const updatedColumns = [...configs];
    const [draggedColumn] = updatedColumns.splice(dragIndex, 1);
    updatedColumns.splice(hoverIndex, 0, draggedColumn);

    updatedColumns.forEach((column, index) => {
      column.sortOrder = index;
    });

    setConfigs(updatedColumns);
   
    triggerOnChange(updatedColumns);
  }, [configs]);

  const handleSave = async () => {
    const updatedColumns = triggerOnChange(configs);
    const configsWithScreen = configs.map(column => {
      // Update the attribute you need, e.g., 'width' or another attribute
      return {
        ...column, // Spread existing column properties
        screen: screenName
      };
    });

    await upsertTableConfig(configsWithScreen);
    onChange(updatedColumns, true);
  };

  return (
    <DndProvider backend={HTML5Backend}>
      <div>
      <span className="badge badge-light-info text-wrap lh-sm text-start">
        {'You can drag the order of columns to your requirement!'.toTranslatedDisplay()}</span>
        {configs.map((config, index) => (
          <DraggableColumn
            key={config.fieldName as string}
            index={index}
            config={config}
            moveColumn={moveColumn}
            handleColumnChange={handleColumnChange}
          />
        ))}
        <button className='btn btn-sm btn-primary mt-2' onClick={handleSave}>{'Save'.toTranslatedDisplay()}</button>
      </div>
    </DndProvider>
  );
};


